import { supabase } from './client'
import {
  RelatedAppInsert,
  RelatedAppUpdate,
  Task,
  TaskAttachment,
  TaskAttachmentInsert,
  TaskInsert,
  TaskNote,
  TaskNoteInsert,
  TaskUpdate,
  ProfileUpdate,
  TaskRow,
  RelatedAppRecord,
  TaskPrerequisite,
  TaskStatusDefaultDays,
} from '../types/database.types'

type TaskRowWithRelation = TaskRow & {
  related_apps: RelatedAppRecord | null
}

const hydrateTask = (task: TaskRowWithRelation): Task => ({
  ...task,
  related_app: task.related_apps?.name || 'Unknown App',
  related_app_record: task.related_apps || null,
})

export const mutations = {
  // Create task
  createTask: async (task: TaskInsert): Promise<Task> => {
    const { data, error } = await supabase
      .from('tasks')
      .insert([task])
      .select('*, related_apps(*)')
      .single()

    if (error) {
      console.error('Error creating task:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create')
    return hydrateTask(data as TaskRowWithRelation)
  },

  // Update task
  updateTask: async (taskId: string, updates: TaskUpdate): Promise<Task> => {
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select('*, related_apps(*)')
      .single()

    if (error) {
      console.error('Error updating task:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return hydrateTask(data as TaskRowWithRelation)
  },

  // Delete task
  deleteTask: async (taskId: string): Promise<void> => {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId)

    if (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  },

  createTaskNote: async (note: TaskNoteInsert): Promise<TaskNote> => {
    const { data, error } = await supabase
      .from('task_notes')
      .insert([note])
      .select()
      .single()

    if (error) {
      console.error('Error creating task note:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create note')
    return data
  },

  createTaskAttachment: async (attachment: TaskAttachmentInsert): Promise<TaskAttachment> => {
    const { data, error } = await supabase
      .from('task_attachments')
      .insert([attachment])
      .select()
      .single()

    if (error) {
      console.error('Error creating task attachment:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create attachment')
    return data
  },

  deleteTaskNote: async (noteId: string): Promise<void> => {
    const { error } = await supabase.from('task_notes').delete().eq('id', noteId)

    if (error) {
      console.error('Error deleting task note:', error)
      throw error
    }
  },

  // Update profile
  updateProfile: async (userId: string, updates: ProfileUpdate) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating profile:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return data
  },

  createRelatedApp: async (app: RelatedAppInsert): Promise<RelatedAppRecord> => {
    const { data, error } = await supabase
      .from('related_apps')
      .insert([app])
      .select()
      .single()

    if (error) {
      console.error('Error creating related app:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from create')
    return data
  },

  updateRelatedApp: async (appId: string, updates: RelatedAppUpdate): Promise<RelatedAppRecord> => {
    const { data, error } = await supabase
      .from('related_apps')
      .update(updates)
      .eq('id', appId)
      .select()
      .single()

    if (error) {
      console.error('Error updating related app:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from update')
    return data
  },

  // Batch update tasks (for Kanban drag-and-drop)
  batchUpdateTasks: async (updates: Array<{ id: string; updates: TaskUpdate }>) => {
    const promises = updates.map(({ id, updates: taskUpdates }) =>
      supabase.from('tasks').update(taskUpdates).eq('id', id)
    )

    const results = await Promise.all(promises)

    // Check for errors
    const errors = results.filter((r) => r.error)
    if (errors.length > 0) {
      console.error('Errors in batch update:', errors)
      throw new Error(`${errors.length} tasks failed to update`)
    }
  },

  setTaskPrerequisite: async (taskId: string, prerequisiteTaskId: string | null): Promise<TaskPrerequisite | null> => {
    const { error: deleteError } = await supabase
      .from('task_prerequisites')
      .delete()
      .eq('task_id', taskId)

    if (deleteError) {
      console.error('Error clearing task prerequisites:', deleteError)
      throw deleteError
    }

    if (!prerequisiteTaskId) {
      return null
    }

    const { data, error } = await supabase
      .from('task_prerequisites')
      .insert([{ task_id: taskId, prerequisite_task_id: prerequisiteTaskId }])
      .select()
      .single()

    if (error) {
      console.error('Error creating task prerequisite:', error)
      throw error
    }

    return data
  },

  upsertTaskStatusDefaultDays: async (status: string, defaultDays: number): Promise<TaskStatusDefaultDays> => {
    const { data, error } = await supabase
      .from('task_status_default_days')
      .upsert([{ status, default_days: defaultDays }], { onConflict: 'status' })
      .select()
      .single()

    if (error) {
      console.error('Error upserting task status default days:', error)
      throw error
    }

    if (!data) throw new Error('No data returned from upsert task status default days')
    return data
  },
}
